import { useMemo, useState } from "react";

import { swSettings } from "app/common/services/swSettings";
import { i18nFilter } from "app/filters/i18nFilter";
import { InviteUsersBanner } from "app/pages/digital-suite/home-page/components/InviteUsers/Banner/InviteUsersBanner";
import {
    getLocalHideInviteUsersBanner,
    getSessionHideInviteUsersBanner,
    localHideInviteUsersBanner,
    sessionHideInviteUsersBanner,
    inviteUsersToRegister,
    requestAccess,
} from "app/pages/digital-suite/home-page/components/InviteUsers/InviteUsersApi";
import InvitationTypeModal from "app/pages/digital-suite/home-page/components/InviteUsers/Modal/InvitationTypeModal/InvitationTypeModal";
import { InviteUsersModal } from "app/pages/digital-suite/home-page/components/InviteUsers/Modal/InviteUsersModal/InviteUsersModal";
import useModalsState from "app/pages/digital-suite/home-page/components/InviteUsers/Modal/UseModalState";
import type {
    IInviteUsersTranslations,
    IUserInvitation,
} from "app/pages/digital-suite/home-page/components/InviteUsers/types";
import { EModalTypeOpen } from "app/pages/digital-suite/home-page/components/InviteUsers/types";
import { renderInvitationButtonUI } from "app/pages/digital-suite/home-page/components/InviteUsers/utils";
import InviteUsersV2 from "app/pages/digital-suite/home-page/components/InviteUsersV2";
import { allTrackers } from "app/services/track/track";
import { userRoleTitle } from "app/UtilitiesAndConstants/UtilityFunctions/userRoleUtils";
import { isNoTouch, isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

const isInviteUsersV2Enabled = true;

export const useUserInvitation = ({
    hasAvailableAllocations,
    isNewHomepage = false,
    tooltipContent,
    onOpenModal,
}: {
    hasAvailableAllocations: boolean;
    isNewHomepage?: boolean;
    tooltipContent?: string;
    onOpenModal?: () => void;
}): IUserInvitation => {
    const i18n = i18nFilter();

    const isTrialUser = isTrial(swSettings);
    const isNoTouchUser = isNoTouch(swSettings);
    const isTouchUser = !isTrialUser && !isNoTouchUser;

    const isYellowAccount = swSettings.components.Home.resources.AllowDirectInviteByUsers && isTouchUser;
    const isUnlimitedSeats =
        swSettings.components.Home.resources.AllowUnlimitedSeatsInvitations && hasAvailableAllocations && isTouchUser;

    const isProminent = isYellowAccount || isUnlimitedSeats;
    const shouldShowInvitationFeatures = isTrialUser || isNoTouchUser || isYellowAccount || isUnlimitedSeats;

    const shouldHideBanner = useMemo(
        () => (isNoTouchUser ? getLocalHideInviteUsersBanner() : getSessionHideInviteUsersBanner()),
        [isNoTouchUser],
    );

    const [showBanner, setShowBanner] = useState<boolean>(shouldShowInvitationFeatures && !shouldHideBanner);

    const [modalsState, setModalState] = useModalsState({
        [EModalTypeOpen.INVITE_USERS_MODAL_OPEN]: false,
        [EModalTypeOpen.INVITATION_TYPE_MODAL_OPEN]: false,
    });

    const texts = useMemo<IInviteUsersTranslations>(() => {
        if (isTrialUser || isNoTouchUser) {
            return {
                button: {
                    tooltipContent: i18n("growth.homepage.inviteUsers.button.tooltip"),
                },
                banner: {
                    title: i18n("growth.homepage.inviteUsers.banner.title"),
                    subtitle: i18n("growth.homepage.inviteUsers.banner.subtitle"),
                    buttonText: i18n("growth.homepage.inviteUsers.banner.button"),
                },
                modal: {
                    title: i18n("growth.homepage.inviteUsers.modal.title"),
                    description: i18n("growth.homepage.inviteUsers.modal.description"),
                    placeholder: i18n("growth.homepage.inviteUsers.modal.fieldPlaceholder"),
                    errorMessage: i18n("growth.homepage.inviteUsers.modal.error"),
                    sendButtonText: i18n("growth.homepage.inviteUsers.modal.send"),
                },
            };
        } else {
            return {
                button: {
                    tooltipContent: i18n("growth.homepage.inviteUsers.button.tooltip.touch"),
                },
                banner: {
                    title: isUnlimitedSeats
                        ? i18n("growth.homepage.inviteUsers.banner.title.touch.unlimited")
                        : i18n("growth.homepage.inviteUsers.banner.title.touch"),
                    subtitle: isUnlimitedSeats ? "" : i18n("growth.homepage.inviteUsers.banner.subtitle.touch"),
                    buttonText: isUnlimitedSeats
                        ? i18n("growth.homepage.inviteUsers.banner.button.touch.unlimited")
                        : i18n("growth.homepage.inviteUsers.banner.button.touch"),
                },
                modal: {
                    title: i18n("growth.homepage.inviteUsers.modal.title.touch"),
                    description: i18n("growth.homepage.inviteUsers.modal.description.touch"),
                    placeholder: i18n("growth.homepage.inviteUsers.modal.fieldPlaceholder.touch"),
                    errorMessage: i18n("growth.homepage.inviteUsers.modal.error.touch"),
                    sendButtonText: i18n("growth.homepage.inviteUsers.modal.send.touch"),
                },
            };
        }
    }, [isTrialUser, isNoTouchUser]);

    const handleOpenModal = () => {
        const isAdmin = swSettings?.user?.userRole === userRoleTitle.ADMIN;
        if (isInviteUsersV2Enabled) {
            allTrackers.dispatchTrackEvent("InviteUsersV2", "Invite Teammate", `isAdmin: ${isAdmin}`);
            if (isAdmin) {
                const url = `${swSettings.swsites.login}/user-management?OpenInvite=true`;
                window.open(url, "_blank");
                allTrackers.dispatchTrackEvent("InviteUsersV2", "Invite", "click Admin");
                return;
            } else {
                setModalState(EModalTypeOpen.INVITE_USERS_MODAL_OPEN, true);
            }
        } else {
            setModalState(
                isNoTouchUser ? EModalTypeOpen.INVITATION_TYPE_MODAL_OPEN : EModalTypeOpen.INVITE_USERS_MODAL_OPEN,
                true,
            );
            setModalState(EModalTypeOpen.INVITATION_TYPE_MODAL_OPEN, true);
        }
        if (onOpenModal) {
            onOpenModal();
        }
    };

    const renderInvitationButton = () => {
        if (!shouldShowInvitationFeatures) {
            return null;
        }

        const onClick = () => {
            allTrackers.dispatchTrackEvent("Button", "click", "Invite Users/Icon/Open");
            handleOpenModal();
        };

        return renderInvitationButtonUI({
            tooltipContent: tooltipContent ? tooltipContent : texts.button.tooltipContent,
            onClick,
            isProminent,
            isNewHomepage,
        });
    };

    const renderInvitationBanner = () => {
        if (!showBanner || !shouldShowInvitationFeatures || (isYellowAccount && !isUnlimitedSeats)) {
            return null;
        }

        return (
            <InviteUsersBanner
                onClick={() => {
                    allTrackers.dispatchTrackEvent("Button", "click", "Invite Users/Banner/Open");
                    setModalState(EModalTypeOpen.INVITE_USERS_MODAL_OPEN, true);
                }}
                onClose={() => {
                    allTrackers.dispatchTrackEvent("Button", "click", "Invite Users/Banner/Close");
                    setShowBanner(false);

                    if (isNoTouchUser) {
                        localHideInviteUsersBanner();
                    } else {
                        sessionHideInviteUsersBanner();
                    }
                }}
                isProminent={isProminent}
                {...texts.banner}
            />
        );
    };

    const onInvitationTypeModalNextClick = (invitationSelectedId: string) => {
        setModalState(EModalTypeOpen.INVITATION_TYPE_MODAL_OPEN, false);
        switch (invitationSelectedId) {
            case "collaborate":
                allTrackers.trackEvent("Button", "click", "Invite Users/Collaborate or Invite/Collaborate");
                const addOnsLink = `${swSettings.swsites.login}${swSettings.components.HookWorkspace.resources.NtAddOnPackagesUrl}?packageType=1`;
                window.open(addOnsLink, "_blank");
                break;
            case "sendTrial":
                allTrackers.trackEvent("Button", "click", "Invite Users/Collaborate or Invite/Invite");
                setModalState(EModalTypeOpen.INVITE_USERS_MODAL_OPEN, true);
                break;
            default:
                break;
        }
    };

    const onInvitationTypeModalClose = () => {
        allTrackers.trackEvent("Button", "click", "Invite Users/Collaborate or Invite/Cancel");
        setModalState(EModalTypeOpen.INVITATION_TYPE_MODAL_OPEN, false);
    };

    const onInviteUsersModalClose = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Invite Users/Modal/Cancel");
        setModalState(EModalTypeOpen.INVITE_USERS_MODAL_OPEN, false);
    };

    const renderInvitationModal = () => {
        if (isInviteUsersV2Enabled) {
            return (
                <InviteUsersV2
                    open={modalsState[EModalTypeOpen.INVITE_USERS_MODAL_OPEN]}
                    onClose={onInviteUsersModalClose}
                />
            );
        }
        return (
            <>
                {modalsState[EModalTypeOpen.INVITATION_TYPE_MODAL_OPEN] ? (
                    <InvitationTypeModal
                        open={modalsState[EModalTypeOpen.INVITATION_TYPE_MODAL_OPEN]}
                        onClose={onInvitationTypeModalClose}
                        onNextClick={onInvitationTypeModalNextClick}
                    />
                ) : null}
                {modalsState[EModalTypeOpen.INVITE_USERS_MODAL_OPEN] ? (
                    <InviteUsersModal
                        open={modalsState[EModalTypeOpen.INVITE_USERS_MODAL_OPEN]}
                        onClose={onInviteUsersModalClose}
                        onSubmit={isUnlimitedSeats ? requestAccess : inviteUsersToRegister}
                        showLeanMoreLink={!isTrialUser ? !isNoTouchUser : null}
                        showCopyLink={!isUnlimitedSeats}
                        inviteUsersScreenProps={texts.modal}
                    />
                ) : null}
            </>
        );
    };

    return {
        invitationButtonComponent: renderInvitationButton(),
        invitationBannerComponent: renderInvitationBanner(),
        invitationModalComponent: renderInvitationModal(),
        isProminent,
        handleOpenModal,
    };
};
